import { lazy, memo, Suspense, useCallback, useEffect, useRef, ReactNode } from "react";
import { useDispatch, Provider } from "react-redux";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { HotkeysProvider } from "react-hotkeys-hook";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import { withAuthInfo, AuthProvider, WithAuthInfoProps, User } from "@propelauth/react";
import { useAnalytics, POSTHOG_EVENTS, PERMISSIONS, useOrgCheck, OrgPicker } from "@repo/ping-react-js";

const PingFileDownloadView = lazy(() => import("src/features/sal/PingFileDownloadView"));
const PingRedirectFromLegacyPage = lazy(() => import("features/modes/PingRedirectFromLegacyPage"));
const SOVSubmissionLogWrapper = lazy(() => import("src/features/sal/SOVSubmissionLogWrapper"));
const SOVDataRelevanceSummaryReport = lazy(() => import("features/sov-report/SOVDataRelevanceSummaryReport"));
const PingMapOverlay = lazy(() => import("features/map-overlay/PingMapOverlay"));
const PingMapWorkspace = lazy(() => import("src/features/map-workspace/PingMapWorkspace"));
const SOVReadOnlyView = lazy(() => import("features/sov-scrubber/SOVReadOnlyView"));
const PLILandingPage = lazy(() => import("src/components/PLILandingPage"));

import {
  setAccessToken,
  PingToaster,
  useQuery,
  RouterContext,
  PingAppsHomePage,
  MeApiResponse,
  useHistoryAdapter,
  DebugTopBanner,
  APPS_HOME_URL,
} from "@repo/ping-react-js";
import { setupStore } from "services/store";
import {
  useGetEnvQuery,
  useGetLayerCookiesQuery,
  useGetAnnotationQuery,
  useGetClientUsageQuery,
  useGetMeQuery,
} from "services/appSlice";
import { getLocationsRemaining } from "utils/clientUsage";
import { setAnnotations } from "reducers/geocoding";
import { setUser } from "reducers/settings";
import { useItemKey, usePLI, getReadonlyTokenFromCookie, useSovid } from "utils/hooks";
import { SOVSummaryMode } from "src/constants/MiscConstants";
import { ErrorPage, PageLayout } from "@repo/ping-design";
import { getSovidFromUrlIfYouDontHaveReactRouter } from "utils/non-authed-utils";
import { getApiBaseUrl } from "services/utils";
import { IS_PAID_LOCATION_ENABLED } from "constants/FeatureFlags";
import EmailResendErrorPage from "./EmailResendErrorPage";
import AnalyticsConsentManager from "./AnalyticsConsentManager";
import type { AnalyticsConsentDecision } from "utils/analyticsConsent";

const store = setupStore();

// V2 redirect component for migrating from old /v2/ routes
const V2Redirect = () => {
  const location = useLocation();
  const id = new URLSearchParams(location.search).get("sovid");
  return id ? <Navigate to={`/maps/${id}`} replace state={{ from: location }} /> : <Navigate to="/maps" replace />;
};

const isLocationLanding = () =>
  IS_PAID_LOCATION_ENABLED &&
  (window.location.hostname.startsWith("location.") || new URLSearchParams(window.location.search).has("landing"));

// Only headless report captures and the report design tool load this page, and a banner would end up in the image.
const isMapOverlayCapture = () => window.location.pathname.startsWith("/map-overlay");

const getSamlUrl = (client: string | null): string =>
  client
    ? `${import.meta.env.VITE_APP_AUTH_URL}/api/fe/v3/login/saml?org_name=${client}&rt=${btoa(window.location.href)}`
    : `${import.meta.env.VITE_APP_AUTH_URL}/api/fe/v3/login/saml?rt=${btoa(window.location.href)}`;

const PropAuthWrapper = ({
  isLoggedIn,
  accessToken,
  user,
  orgHelper,
  isImpersonating,
  impersonatorUserId,
}: WithAuthInfoProps) => {
  const dispatch = useDispatch();
  const { identifyPostHogUser, captureEvent } = useAnalytics();
  const query = useQuery();
  const client = query.get("client");
  const urlToken = query.get("token"); // read-only token from URL
  const cookieToken = getReadonlyTokenFromCookie();

  // Consent must use /me for this token, never the previous account's cached response.
  const { currentData: me, isLoading: isMeLoading } = useGetMeQuery(
    { accessToken: accessToken! },
    { skip: !accessToken },
  );

  useEffect(() => {
    if (user) {
      dispatch(setUser(user));
    }
  }, [user, dispatch]);

  const handleAnalyticsConsentApplied = useCallback(
    (decision: AnalyticsConsentDecision) => {
      if (decision === "accepted" && !isImpersonating && !me?.impersonator_user_id && user && me?.posthog_guid) {
        identifyPostHogUser(user, me, me.posthog_guid);
      }
    },
    [identifyPostHogUser, isImpersonating, me, user],
  );

  const withAnalyticsConsent = (content: ReactNode) => (
    <>
      {content}
      {!isMapOverlayCapture() && (
        <AnalyticsConsentManager
          accessToken={accessToken}
          consentSubject={user?.userId}
          isLoggedIn={isLoggedIn}
          isImpersonating={isImpersonating}
          isMeLoading={isMeLoading}
          me={me}
          onConsentApplied={handleAnalyticsConsentApplied}
        />
      )}
    </>
  );

  const readOnlyToken = urlToken || cookieToken;

  const { orgState, joinableOrgs, joinOrg } = useOrgCheck({
    isLoggedIn,
    orgHelper,
    skip: !!readOnlyToken,
    captureEvent,
  });

  // if not logged in AND no read-only token → kick off SAML
  if (!isLoggedIn && !readOnlyToken) {
    if (isLocationLanding()) {
      return withAnalyticsConsent(
        <Suspense fallback={null}>
          <PLILandingPage />
        </Suspense>,
      );
    }
    window.location.href = getSamlUrl(client);
    return null;
  }
  // Determine which access token to use:
  // 1. If a read-only token exists (URL or cookie), do NOT use the logged-in access token
  // 2. If logged in normally, use the PropelAuth accessToken
  // 3. Otherwise undefined
  const effectiveAccessToken = readOnlyToken ? undefined : isLoggedIn ? accessToken! : undefined;

  // Expose the read-only token (from URL or cookie) to the app when present.
  const effectiveReadOnlyToken = readOnlyToken || undefined;

  if (orgState === "checking") return withAnalyticsConsent(null);
  if (orgState === "picking") {
    return withAnalyticsConsent(<OrgPicker orgs={joinableOrgs} onSelect={joinOrg} />);
  }

  return withAnalyticsConsent(
    <RootWrapper
      accessToken={effectiveAccessToken}
      user={user}
      authenticatedMe={me}
      readOnlyToken={effectiveReadOnlyToken}
      isImpersonating={isImpersonating}
      impersonatorUserId={impersonatorUserId ?? null}
    />,
  );
};

const RootWrapper = memo(
  ({
    accessToken,
    readOnlyToken,
    user,
    authenticatedMe,
    isImpersonating,
    impersonatorUserId,
  }: {
    accessToken?: string;
    readOnlyToken?: string;
    user?: User;
    authenticatedMe?: MeApiResponse;
    isImpersonating?: boolean;
    impersonatorUserId?: string | null;
  }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const history = useHistoryAdapter(navigate);
    const query = useQuery();
    const client = query.get("client");
    const hideTopBar = query.get("hideTopBar") === "1";
    const { sn, srn } = useItemKey();
    const sovid = useSovid();
    const { isPLI } = usePLI();
    const { captureEvent, captureShareableMapLinkOpened } = useAnalytics();
    const capturedShareableLinkRef = useRef<string | null>(null);
    const capturedChargebeeCheckoutRef = useRef<string | null>(null);

    // Use getEnv query to validate the access token (skip if we have read-only token)
    // Note: RootWrapper is rendered above <Routes>, so useParams() can't access
    // the :sovid route param. This means useItemKey().sovId and useSovid() both
    // return null for route-param URLs (e.g. /maps/<sovid>). We fall back to
    // getSovidFromUrlIfYouDontHaveReactRouter() to parse the ID from the raw URL.
    // We also can't use useSovid() for the env query because it would be circular —
    // useSovid() reads resolvedSovid from Redux, which is populated by this query.
    const { data: validationEnvData, error: envError } = useGetEnvQuery(
      {
        sovid: getSovidFromUrlIfYouDontHaveReactRouter(window.location.href),
        accessToken,
      },
      {
        skip: !accessToken || !!readOnlyToken, // Skip if no access token OR if we have read-only token
      },
    );

    const {
      data: me,
      isLoading: isMeLoading,
      isError: isMeError,
    } = useGetMeQuery({ accessToken: accessToken! }, { skip: !accessToken });

    const pingDataPlan = me?.ping_data_plan;
    const shouldFetchClientUsage = IS_PAID_LOCATION_ENABLED && isPLI && !!accessToken && pingDataPlan != null;
    const { data: clientUsage } = useGetClientUsageQuery(
      { accessToken: accessToken! },
      { skip: !shouldFetchClientUsage },
    );

    useEffect(() => {
      if (!clientUsage || !pingDataPlan) return;

      const callsRemaining = getLocationsRemaining(clientUsage);
      if (callsRemaining == null) return;

      console.log("Ping.Location calls remaining:", callsRemaining);
    }, [clientUsage, pingDataPlan]);

    useEffect(() => {
      if (accessToken) {
        dispatch(setAccessToken(accessToken));
        if (envError) {
          console.error("Token validation failed:", envError);
          console.log("Invalid or expired access token - treating as no token");
          // Don't set the token in Redux if validation fails
        }
      }
    }, [accessToken, dispatch, validationEnvData, envError]);

    const token = accessToken ? undefined : readOnlyToken || query.get("token");
    const routeSovid = getSovidFromUrlIfYouDontHaveReactRouter(window.location.href);

    const { data: currentEnvData } = useGetEnvQuery(
      {
        sovid: routeSovid,
        readOnlyToken: token,
        accessToken,
      },
      { skip: !readOnlyToken && !accessToken },
    );

    useGetLayerCookiesQuery(
      {
        sovid: routeSovid,
        readOnlyToken: token,
        accessToken,
      },
      { skip: !readOnlyToken && !accessToken },
    );

    const envData = validationEnvData ?? currentEnvData;
    const analyticsMe = me ?? authenticatedMe;

    useEffect(() => {
      const checkoutStatus = query.get("state");
      if (checkoutStatus !== "success") return;

      const captureKey = `${location.pathname}:${location.search}`;
      if (capturedChargebeeCheckoutRef.current === captureKey) return;
      capturedChargebeeCheckoutRef.current = captureKey;

      captureEvent(POSTHOG_EVENTS.PING_DATA_UPGRADE_COMPLETED, {
        checkout_status: checkoutStatus,
        checkout_provider: "chargebee",
      });
    }, [captureEvent, location.pathname, location.search, query]);

    useEffect(() => {
      if (!readOnlyToken || !envData?.shareable_link_analytics) return;

      // Not keyed on location.search: panning/zooming rewrites center/zoom there, which isn't a new open.
      const captureKey = `${readOnlyToken}:${location.pathname}`;
      if (capturedShareableLinkRef.current === captureKey) return;
      capturedShareableLinkRef.current = captureKey;

      void captureShareableMapLinkOpened({
        readOnlyToken,
        shareableLinkAnalytics: envData.shareable_link_analytics,
        user,
        me: analyticsMe,
      });
    }, [
      analyticsMe,
      captureShareableMapLinkOpened,
      envData?.shareable_link_analytics,
      location.pathname,
      readOnlyToken,
      user,
    ]);

    useEffect(() => {
      if (import.meta.env.VITE_APP_ENV !== "local") {
        Sentry.init({
          dsn: "https://35915db3d76364a244a9dd971282d9f1@o484666.ingest.sentry.io/4506662371786752",
          environment: import.meta.env.VITE_APP_ENV,
          integrations: [
            Sentry.browserTracingIntegration({
              shouldCreateSpanForRequest: (url) => !url.startsWith("https://maps.gstatic.com"),
            }),
          ],
          tracesSampleRate: 1.0,
          debug: false, //!["prd", "prdeu"].includes(import.meta.env.VITE_APP_ENV),
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
          release: import.meta.env.REACT_APP_VERSION || "0.0.0",
        });
      } else {
        Sentry.init({ dsn: null, enabled: false });
      }
    }, []);

    const { data: annotationData } = useGetAnnotationQuery(
      { sovid: sovid, accessToken, readOnlyToken },
      { skip: !sovid || isPLI || (!accessToken && !readOnlyToken) },
    );
    useEffect(() => {
      if (!annotationData) return;
      const current = annotationData.locations.find(
        (loc) => loc.sheet_row_number.toString() === srn && loc.sheet_name === sn,
      );
      if (current) {
        dispatch(setAnnotations(current.annotations));
      }
    }, [annotationData, dispatch, sn, srn]);

    // If we have an access token but validation failed, or no tokens at all, show the auth page
    if ((!accessToken && !readOnlyToken) || (accessToken && envError)) {
      return (
        <ErrorPage type="CUSTOM" mainText="Verifying Credentials">
          <div>Wait a moment…</div>
          <div>
            Or click <a href={getSamlUrl(client)}>here</a> to log in manually.
          </div>
        </ErrorPage>
      );
    }

    return (
      <HotkeysProvider>
        <RouterContext.Provider value={{ location, history }}>
          {!hideTopBar && (
            <DebugTopBanner
              apiBaseUrl={getApiBaseUrl()}
              app="maps"
              accessToken={accessToken ?? null}
              ssoUser={user ?? null}
              me={me}
              isImpersonating={isImpersonating}
              impersonatorUserId={impersonatorUserId ?? null}
            />
          )}
          <Routes>
            <Route path="/consent" element={<Navigate to="/" replace />} />
            <Route path="/v2/*" element={<V2Redirect />} />
            <Route
              path="/g"
              element={
                <Suspense fallback={null}>
                  <PingRedirectFromLegacyPage />
                </Suspense>
              }
            />
            <Route
              path="/geocoding"
              element={
                <Suspense fallback={null}>
                  <PingRedirectFromLegacyPage />
                </Suspense>
              }
            />
            <Route
              path="/maps/g"
              element={
                <Suspense fallback={null}>
                  <PingRedirectFromLegacyPage />
                </Suspense>
              }
            />
            <Route
              path="/maps/geocoding"
              element={
                <Suspense fallback={null}>
                  <PingRedirectFromLegacyPage />
                </Suspense>
              }
            />
            <Route
              path="/download"
              element={
                <Suspense fallback={null}>
                  <PingFileDownloadView />
                </Suspense>
              }
            />
            <Route path="/activity" element={<SALAccessRedirect me={me} isMeLoading={isMeLoading} />} />
            <Route
              path="/sovfixer/activity"
              element={
                <Suspense fallback={null}>
                  <ProtectedSALRoute me={me} isMeLoading={isMeLoading}>
                    <SOVSubmissionLogWrapper />
                  </ProtectedSALRoute>
                </Suspense>
              }
            />
            <Route path="/sal" element={<SALAccessRedirect me={me} isMeLoading={isMeLoading} />} />
            <Route
              path="/new/maps/pli"
              element={
                <Suspense fallback={null}>
                  <ProtectedMapsRoute me={me} isMeLoading={isMeLoading} isMeError={isMeError}>
                    <PingMapWorkspace />
                  </ProtectedMapsRoute>
                </Suspense>
              }
            />
            <Route
              path="/new/maps/:sovid?"
              element={
                <Suspense fallback={null}>
                  <ProtectedMapsRoute me={me} isMeLoading={isMeLoading} isMeError={isMeError}>
                    <PingMapWorkspace />
                  </ProtectedMapsRoute>
                </Suspense>
              }
            />
            <Route
              path="/maps/pli"
              element={
                <Suspense fallback={null}>
                  <ProtectedMapsRoute me={me} isMeLoading={isMeLoading} isMeError={isMeError}>
                    <PingMapWorkspace />
                  </ProtectedMapsRoute>
                </Suspense>
              }
            />
            <Route
              path="/maps/:sovid?"
              element={
                <Suspense fallback={null}>
                  <ProtectedMapsRoute me={me} isMeLoading={isMeLoading} isMeError={isMeError}>
                    <PingMapWorkspace />
                  </ProtectedMapsRoute>
                </Suspense>
              }
            />
            <Route
              path="/map"
              element={
                <Suspense fallback={null}>
                  <ProtectedMapsRoute me={me} isMeLoading={isMeLoading} isMeError={isMeError}>
                    <PingMapWorkspace />
                  </ProtectedMapsRoute>
                </Suspense>
              }
            />
            <Route
              path="/sov-report/:sovid"
              element={
                <Suspense fallback={null}>
                  <PageLayout title="SOV Summary Report">
                    <SOVDataRelevanceSummaryReport loadState={SOVSummaryMode.PAGE} />
                  </PageLayout>
                </Suspense>
              }
            />
            <Route
              path="/map-overlay/:sovid?"
              element={
                <Suspense fallback={null}>
                  <PingMapOverlay />
                </Suspense>
              }
            />
            <Route
              path="/sov/read-only"
              element={
                <Suspense fallback={null}>
                  <SOVReadOnlyView />
                </Suspense>
              }
            />
            <Route path="/" element={<PingHomeGate me={me} />} />
            <Route path="*" element={<ErrorPage type="NOT_FOUND" />} />
          </Routes>
        </RouterContext.Provider>
      </HotkeysProvider>
    );
  },
);

const ProtectedSALRoute = ({
  me,
  isMeLoading,
  children,
}: {
  me?: MeApiResponse;
  isMeLoading: boolean;
  children: ReactNode;
}) => {
  if (isMeLoading && !me) {
    return null;
  }

  if (!me?.permissions?.includes(PERMISSIONS.CanAccessPingActivityLog)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const ProtectedMapsRoute = ({
  me,
  isMeLoading,
  isMeError,
  children,
}: {
  me?: MeApiResponse;
  isMeLoading: boolean;
  isMeError: boolean;
  children: ReactNode;
}) => {
  const query = useQuery();
  const { isPLI } = usePLI();
  const readOnlyToken = query.get("token");

  // If we have a read-only token, allow access without permission checks
  if (readOnlyToken) {
    return <>{children}</>;
  }

  if (isMeLoading && !me) {
    return null;
  }

  if (isMeError) {
    console.error("Failed to load /api/v1/me; cannot verify Ping Maps access");
    return (
      <ErrorPage
        type="CUSTOM"
        mainText="Couldn't load your account"
        buttonLabel="Reload"
        onClick={() => window.location.reload()}
      >
        We couldn't verify your access to Ping Maps. If this keeps happening, please contact support@pingintel.com.
      </ErrorPage>
    );
  }

  if (isPLI) {
    if (!me?.permissions?.includes(PERMISSIONS.CanAccessPingLocation)) {
      console.error("Access denied: missing permission can_access_ping_location");
      return (
        <ErrorPage
          type="CUSTOM"
          mainText="Access Denied"
          buttonLabel="Go to Ping Apps"
          onClick={() => window.location.assign(APPS_HOME_URL)}
        >
          You do not have permission to access Ping Location Intelligence (can_access_ping_location).
        </ErrorPage>
      );
    }
  } else {
    if (!me?.permissions?.includes(PERMISSIONS.CanAccessPingMaps)) {
      console.error("Access denied: missing permission can_access_ping_maps");
      return (
        <ErrorPage
          type="CUSTOM"
          mainText="Access Denied"
          buttonLabel="Go to Ping Apps"
          onClick={() => window.location.assign(APPS_HOME_URL)}
        >
          You do not have permission to access Ping Maps (can_access_ping_maps).
        </ErrorPage>
      );
    }
  }

  return <>{children}</>;
};

const SALAccessRedirect = ({ me, isMeLoading }: { me?: MeApiResponse; isMeLoading: boolean }) => {
  if (isMeLoading && !me) {
    return null;
  }

  if (me?.permissions?.includes(PERMISSIONS.CanAccessPingActivityLog)) {
    return <Navigate to="/sovfixer/activity" replace />;
  }

  return <Navigate to="/" replace />;
};

export const PingHomeGate = ({ me }: { me?: MeApiResponse }) => {
  if (window.location.hostname.startsWith("location.")) {
    return <Navigate to="/maps/pli" replace />;
  }

  const isFreeUser =
    me?.permissions?.includes(PERMISSIONS.CanAccessPingLocation) &&
    !me?.permissions?.includes(PERMISSIONS.CanAccessPingMaps);

  if (isFreeUser) {
    return <Navigate to="/maps/pli" replace />;
  }

  return <PingAppsHomePage me={me} />;
};

const WrappedPropAuth = withAuthInfo(PropAuthWrapper);

const Root = () => (
  <AuthProvider authUrl={import.meta.env.VITE_APP_AUTH_URL || ""}>
    <Provider store={store}>
      <BrowserRouter>
        <HelmetProvider>
          <PingToaster />
          <Routes>
            <Route path="/email-resend-error" element={<EmailResendErrorPage />} />
            <Route path="*" element={<WrappedPropAuth />} />
          </Routes>
        </HelmetProvider>
      </BrowserRouter>
    </Provider>
  </AuthProvider>
);

export default Root;
